/**
 * Anchored floating panel — the base of menus and pickers.
 *
 * Portalled to <body> with fixed positioning, so it escapes overflow and
 * sticky containers (the review toolbar, scrolling tables, the top bar). It
 * flips above the anchor when there is no room below and is clamped to the
 * viewport; it repositions on scroll (capture phase — any scroller) and
 * resize. Position is written straight to the DOM node, never through state.
 *
 * Closing: Escape (via the layer stack), a pointerdown outside both the
 * panel and the anchor (the anchor is excluded so its own click can toggle),
 * Tab leaving the panel, or the caller after a selection. Focus returns to
 * the anchor on Escape and selection, not on an outside click — the click
 * already put focus where the operator meant it.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent,
  type PointerEventHandler,
  type ReactNode,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";

import { markOutsideDismiss, useLayer } from "../../lib/layers";
import { focusableWithin } from "../../lib/hooks/useFocusTrap";

export type PopoverPlacement = "bottom-start" | "bottom-end" | "top-start" | "top-end";
export type PopoverCloseReason = "escape" | "outside" | "select" | "tab";

interface Props {
  open: boolean;
  onClose: (reason: PopoverCloseReason) => void;
  anchorRef: RefObject<HTMLElement | null>;
  placement?: PopoverPlacement;
  offset?: number;
  role?: "menu" | "dialog";
  id?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  /** What gets focus on open: the first focusable child, the panel, a ref,
   * or nothing (`"none"` – a hover-opened menu leaves focus where it is). */
  initialFocus?: "first" | "container" | "none" | RefObject<HTMLElement | null>;
  className?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onPointerEnter?: PointerEventHandler<HTMLDivElement>;
  onPointerLeave?: PointerEventHandler<HTMLDivElement>;
  children: ReactNode;
}

const MARGIN = 8;

function position(panel: HTMLElement, anchor: HTMLElement, placement: PopoverPlacement, offset: number) {
  const a = anchor.getBoundingClientRect();
  const width = panel.offsetWidth;
  const height = panel.offsetHeight;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const wantsTop = placement.startsWith("top");
  const roomBelow = vh - a.bottom - offset - MARGIN;
  const roomAbove = a.top - offset - MARGIN;
  const top =
    (wantsTop && roomAbove >= height) || (!wantsTop && roomBelow < height && roomAbove > roomBelow)
      ? a.top - offset - height
      : a.bottom + offset;

  const alignEnd = placement.endsWith("end");
  let left = alignEnd ? a.right - width : a.left;
  left = Math.min(Math.max(left, MARGIN), vw - width - MARGIN);

  panel.style.top = `${Math.max(MARGIN, Math.min(top, vh - height - MARGIN))}px`;
  panel.style.left = `${left}px`;
}

export function Popover({
  open,
  onClose,
  anchorRef,
  placement = "bottom-start",
  offset = 4,
  role,
  id,
  initialFocus = "first",
  className,
  onKeyDown,
  onPointerEnter,
  onPointerLeave,
  children,
  ...aria
}: Props) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  const isTop = useLayer(open, "popover", () => {
    onCloseRef.current("escape");
    anchorRef.current?.focus({ preventScroll: true });
  });

  useLayoutEffect(() => {
    const panel = panelRef.current;
    const anchor = anchorRef.current;
    if (!open || !panel || !anchor) return;
    const update = () => position(panel, anchor, placement, offset);
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, placement, offset]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!open || !panel) return;
    const raf = window.requestAnimationFrame(() => {
      if (initialFocus === "none" || panel.contains(document.activeElement)) return;
      const target =
        initialFocus === "container"
          ? panel
          : initialFocus === "first"
            ? (focusableWithin(panel)[0] ?? panel)
            : (initialFocus.current ?? focusableWithin(panel)[0] ?? panel);
      target.focus({ preventScroll: true });
    });
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target || panel.contains(target) || anchorRef.current?.contains(target)) return;
      if (!isTop()) return;
      markOutsideDismiss(e.target);
      onCloseRef.current("outside");
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
    // initialFocus is read once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, anchorRef, isTop]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      id={id}
      role={role}
      tabIndex={-1}
      className={clsx("popover", className)}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (!e.defaultPrevented && e.key === "Tab") {
          e.preventDefault();
          onCloseRef.current("tab");
          anchorRef.current?.focus({ preventScroll: true });
        }
      }}
      {...aria}
    >
      {children}
    </div>,
    document.body,
  );
}
